import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Input,
  Layout,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { DatePicker, FormatDate, Header } from "components";
import {
  exportBalanceLots,
  isBreakageMockMode,
  listBalanceLots,
} from "services/breakage-service";
import { formatAmountMinor } from "views/breakage/breakage-money";
import BreakageLotDetailModal from "views/breakage/breakage-lot-detail-modal";
import {
  formatUtcExclusiveToLabel,
  formatUtcInclusiveDayLabel,
  toUtcExclusiveDayRange,
} from "views/breakage/breakage-date-range";
import { downloadBlob } from "views/breakage/breakage-export-download";
import {
  agingColor,
  eligibilityColor,
  labelAging,
  labelEligibility,
  labelOriginType,
} from "views/breakage/breakage-labels";
import { createRequestGuard } from "views/breakage/breakage-request-guard";
import {
  balanceLotFilterKey,
  describeParticipantFilter,
  participantLegalName,
  pickCreditedDateFilter,
  pickParticipantFilter,
  resolveParticipantSearch,
} from "views/breakage/breakage-participant-display";

const { Content } = Layout;
const { Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
const EMPTY_FILTER = {};
const SEARCH_TIP_DISMISSED_KEY =
  "breakage.statement.searchTipDismissed";

const readSearchTipDismissed = () => {
  try {
    return window.localStorage.getItem(SEARCH_TIP_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

const BreakageStatementScreen = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageMeta, setPageMeta] = useState({
    hasMore: false,
    nextCursor: null,
    limit: DEFAULT_PAGE_SIZE,
  });
  const [cursorStack, setCursorStack] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState(EMPTY_FILTER);
  const [searchDraft, setSearchDraft] = useState("");
  const [creditedRangeValue, setCreditedRangeValue] = useState(null);
  const [detailLotId, setDetailLotId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadErrorCode, setLoadErrorCode] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showSearchTip, setShowSearchTip] = useState(
    () => !readSearchTipDismissed()
  );

  const mockMode = isBreakageMockMode();
  const requestGuardRef = useRef(createRequestGuard());
  const activeFilterRef = useRef(activeFilter);
  activeFilterRef.current = activeFilter;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const activeFilterKey = balanceLotFilterKey(activeFilter);
  const filterDescription = describeParticipantFilter(activeFilter);
  const hasCreditedFilter = Boolean(
    activeFilter.creditedFrom || activeFilter.creditedTo
  );

  const loadPage = useCallback(async (cursor, forFilter, limitOverride) => {
    const appliedFilter =
      forFilter !== undefined ? forFilter : activeFilterRef.current;
    const appliedKey = balanceLotFilterKey(appliedFilter);
    const appliedLimit = limitOverride ?? pageSizeRef.current;
    const requestToken = requestGuardRef.current.begin();

    setLoading(true);
    setLoadError(null);
    setLoadErrorCode(null);

    try {
      const result = await listBalanceLots({
        limit: appliedLimit,
        cursor,
        ...appliedFilter,
      });

      if (!requestGuardRef.current.isCurrent(requestToken)) {
        return;
      }
      if (balanceLotFilterKey(activeFilterRef.current) !== appliedKey) {
        return;
      }
      if (pageSizeRef.current !== appliedLimit) {
        return;
      }

      if (!result?.ok) {
        setItems([]);
        setPageMeta({
          hasMore: false,
          nextCursor: null,
          limit: appliedLimit,
        });
        setLoadError(
          result?.unavailable
            ? "API Breakage não configurada ou indisponível."
            : result?.error || "Não foi possível carregar os saldos."
        );
        setLoadErrorCode(result?.code || null);
        return;
      }

      const data = result.data;
      setItems(data?.items || []);
      setPageMeta({
        hasMore: Boolean(data?.page?.hasMore),
        nextCursor: data?.page?.nextCursor ?? null,
        limit: data?.page?.limit ?? appliedLimit,
      });
    } finally {
      if (requestGuardRef.current.isCurrent(requestToken)) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const guard = requestGuardRef.current;
    guard.invalidate();
    setCursorStack([null]);
    setPageIndex(0);
    loadPage(null, activeFilter, pageSize);

    return () => {
      guard.invalidate();
    };
  }, [activeFilterKey, activeFilter, pageSize, loadPage]);

  const onPageSizeChange = (nextSize) => {
    setPageSize(nextSize);
  };

  const onSearch = (value) => {
    const nextDraft = value?.trim() || "";
    setSearchDraft(nextDraft);
    const participant = resolveParticipantSearch(nextDraft);
    // Prefixos muito curtos costumam estourar CADASTRAL_SEARCH_LIMIT_EXCEEDED na API.
    if (participant.legalName && String(participant.legalName).trim().length < 3) {
      message.warning(
        "Informe ao menos 3 caracteres do nome, ou use documento/ID completo."
      );
      return;
    }
    setActiveFilter((prev) => ({
      ...pickCreditedDateFilter(prev),
      ...participant,
    }));
  };

  const onCreditedRangeChange = (dates, dateStrings) => {
    setCreditedRangeValue(dates || null);
    setActiveFilter((prev) => {
      const participant = pickParticipantFilter(prev);
      if (!dateStrings?.[0] || !dateStrings?.[1]) {
        return participant;
      }
      const range = toUtcExclusiveDayRange(dateStrings[0], dateStrings[1]);
      if (!range) {
        return participant;
      }
      return {
        ...participant,
        creditedFrom: range.from,
        creditedTo: range.to,
      };
    });
  };

  const onNextPage = () => {
    if (!pageMeta.hasMore || !pageMeta.nextCursor) {
      return;
    }
    const next = pageMeta.nextCursor;
    setCursorStack((stack) => [...stack.slice(0, pageIndex + 1), next]);
    setPageIndex((i) => i + 1);
    loadPage(next, activeFilter);
  };

  const onPrevPage = () => {
    if (pageIndex === 0) {
      return;
    }
    const prevIndex = pageIndex - 1;
    const prevCursor = cursorStack[prevIndex];
    setPageIndex(prevIndex);
    loadPage(prevCursor, activeFilter);
  };

  const columns = useMemo(
    () => [
      {
        title: "Creditado em",
        dataIndex: "creditedAt",
        key: "creditedAt",
        align: "center",
        width: 140,
        render: (value) => <FormatDate value={value} format="DD/MM/YYYY" />,
      },
      {
        title: "Vence em",
        dataIndex: "expiresAt",
        key: "expiresAt",
        align: "center",
        width: 140,
        render: (value) => <FormatDate value={value} format="DD/MM/YYYY" />,
      },
      {
        title: "Nome",
        key: "legalName",
        align: "center",
        width: 180,
        ellipsis: true,
        render: (_, record) => {
          const name = participantLegalName(record);
          return (
            <span title={record.participantId || undefined}>
              {(name && String(name).trim()) || "—"}
            </span>
          );
        },
      },
      {
        title: "Documento",
        key: "documentNumber",
        align: "center",
        width: 140,
        ellipsis: true,
        render: (_, record) => {
          const doc = record.participant?.documentNumber;
          return (doc && String(doc).trim()) || "—";
        },
      },
      {
        title: "Origem do saldo",
        dataIndex: "originType",
        key: "originType",
        align: "center",
        width: 160,
        render: (value) => labelOriginType(value),
      },
      {
        title: "Saldo disponível",
        dataIndex: "remainingAmount",
        key: "remainingAmount",
        align: "center",
        width: 160,
        render: (value) => formatAmountMinor(value),
      },
      {
        title: "Elegibilidade",
        dataIndex: "eligibility",
        key: "eligibility",
        align: "center",
        width: 170,
        render: (value) => (
          <Tag color={eligibilityColor[value] || "default"}>
            {labelEligibility(value)}
          </Tag>
        ),
      },
      {
        title: "Situação do prazo",
        dataIndex: "agingCondition",
        key: "agingCondition",
        align: "center",
        width: 150,
        render: (value) => (
          <Tag color={agingColor[value] || "default"}>{labelAging(value)}</Tag>
        ),
      },
      {
        title: "Ações",
        key: "actions",
        align: "center",
        width: 120,
        render: (_, record) => (
          <Button
            type="link"
            onClick={() => setDetailLotId(record.balanceLotId)}
          >
            Ver detalhes
          </Button>
        ),
      },
    ],
    []
  );

  const emptyText = (() => {
    if (loadError) {
      return "Não foi possível carregar os saldos.";
    }
    if (filterDescription && hasCreditedFilter) {
      return `Nenhum saldo para ${filterDescription.label} ${filterDescription.value} no período creditado.`;
    }
    if (filterDescription) {
      return `Nenhum saldo para ${filterDescription.label} ${filterDescription.value}.`;
    }
    if (hasCreditedFilter) {
      return "Nenhum saldo no período creditado selecionado.";
    }
    return "Nenhum saldo encontrado.";
  })();

  const onExport = useCallback(async (format) => {
    setExportLoading(true);
    try {
      // Mesmos filtros da listagem; nunca envia limit/cursor/pageSize —
      // o backend pagina internamente e devolve todos os saldos do filtro (cap 5.000).
      const filters = {
        ...pickParticipantFilter(activeFilterRef.current),
        ...pickCreditedDateFilter(activeFilterRef.current),
      };
      const result = await exportBalanceLots({
        format,
        ...filters,
      });
      if (!result?.ok || !result.blob) {
        message.error(
          result?.unavailable
            ? "API Breakage não configurada ou indisponível."
            : result?.error || "Erro ao exportar saldos."
        );
        return;
      }
      downloadBlob(result.blob, result.fileName);
      if (result.truncated) {
        message.warning(
          "Exportação truncada: o arquivo contém no máximo 5.000 saldos."
        );
      } else {
        message.success("Exportação concluída (todos os saldos do filtro).");
      }
    } catch {
      message.error("Erro ao exportar saldos.");
    } finally {
      setExportLoading(false);
    }
  }, []);

  const exportMenuItems = useMemo(
    () => [
      { key: "xlsx", label: "Excel (XLSX)" },
      { key: "csv", label: "CSV" },
      { key: "json", label: "JSON" },
      { key: "xml", label: "XML" },
    ],
    []
  );

  return (
    <Layout className="alymente-layout">
      <Header
        title="Consulta de saldos"
        subtitle="Histórico por participante — útil para explicar saldo vencido ou ainda não liberado"
        routes={[{ label: "Resumo", route: "/breakage" }]}
      >
        {mockMode && (
          <Text type="warning">Modo mock ativo</Text>
        )}
      </Header>

      <Content style={{marginBottom: 16}}>
        {loadError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              loadErrorCode === "CADASTRAL_SEARCH_LIMIT_EXCEEDED"
                ? "Busca por nome ou documento muito ampla"
                : "Consulta de saldos indisponível"
            }
            description={loadError}
            action={
              <Button
                size="small"
                onClick={() =>
                  loadPage(cursorStack[pageIndex] ?? null, activeFilter)
                }
              >
                Tentar novamente
              </Button>
            }
          />
        )}

        {showSearchTip && (
          <Alert
            type="info"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            message="Busca por nome, documento ou ID"
            description={
              <>
                Digite um <strong>prefixo de nome mais específico</strong>{" "}
                (nome e sobrenome), o <strong>documento completo</strong>{" "}
                (CPF/CNPJ) ou o <strong>ID do participante</strong>. Prefixos
                curtos ou comuns podem exceder o limite cadastral da API.
                Opcionalmente filtre por <strong>Creditado em</strong>{" "}
                (início/fim). A API aplica os filtros no servidor antes da
                paginação. <strong>Elegível para Breakage</strong> indica regra
                do lote, não necessariamente saldo já disponível para Redemption
                financeiro.
              </>
            }
            onClose={() => {
              setShowSearchTip(false);
              try {
                window.localStorage.setItem(SEARCH_TIP_DISMISSED_KEY, "1");
              } catch {
                // ignore quota / private mode
              }
            }}
          />
        )}

        <Row
          justify="end"
          align="middle"
          style={{ marginBottom: 16 }}
          gutter={[8, 8]}
        >
          <Col xs={24} sm={12} md={8} lg={7}>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 4 }}
            >
              Creditado em
            </Text>
            <RangePicker
              format="DD/MM/YYYY"
              allowClear
              style={{ width: "100%" }}
              value={creditedRangeValue}
              onChange={onCreditedRangeChange}
              placeholder={["Início", "Fim"]}
            />
          </Col>
          <Col xs={24} sm={12} md={14} lg={12}>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 4 }}
            >
              Participante
            </Text>
            <div
              style={{
                display: "flex",
                gap: 8,
                width: "100%",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <Search
                  allowClear
                  enterButton
                  style={{ width: "100%" }}
                  placeholder="Nome, documento ou ID do participante"
                  value={searchDraft}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSearchDraft(next);
                    if (!next) {
                      setActiveFilter((prev) => pickCreditedDateFilter(prev));
                    }
                  }}
                  onSearch={onSearch}
                />
              </div>
              <Dropdown
                menu={{
                  items: exportMenuItems,
                  onClick: ({ key }) => onExport(key),
                }}
                disabled={exportLoading}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={exportLoading}
                  title="Exporta todos os saldos do filtro atual, não só a página"
                >
                  Exportar
                </Button>
              </Dropdown>
            </div>
          </Col>
        </Row>

        {(filterDescription || hasCreditedFilter) && (
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            {filterDescription && (
              <>
                Filtrando por {filterDescription.label}:{" "}
                <Text code>{filterDescription.value}</Text>
              </>
            )}
            {filterDescription && hasCreditedFilter && " · "}
            {hasCreditedFilter && (
              <>
                Creditado de{" "}
                <Text code>
                  {formatUtcInclusiveDayLabel(activeFilter.creditedFrom)}
                </Text>{" "}
                até{" "}
                <Text code>
                  {formatUtcExclusiveToLabel(activeFilter.creditedTo)}
                </Text>
              </>
            )}
          </Text>
        )}

        {loading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : (
          <Table
            rowKey="balanceLotId"
            columns={columns}
            dataSource={items}
            pagination={false}
            scroll={{ x: 1440 }}
            locale={{ emptyText }}
          />
        )}

        <Row justify="end" style={{ marginTop: 16 }}>
          <Space wrap align="center">
            <Text type="secondary">Página {pageIndex + 1}</Text>
            <Button disabled={pageIndex === 0 || loading} onClick={onPrevPage}>
              Anterior
            </Button>
            <Button
              disabled={!pageMeta.hasMore || loading}
              onClick={onNextPage}
            >
              Próxima
            </Button>
            <Space size={4} align="center">
              <Text type="secondary" id="breakage-statement-page-size-label">
                Por página
              </Text>
              <Select
                id="breakage-statement-page-size"
                value={pageSize}
                disabled={loading}
                style={{ width: 88 }}
                options={PAGE_SIZE_OPTIONS.map((value) => ({
                  value,
                  label: String(value),
                }))}
                onChange={onPageSizeChange}
                aria-labelledby="breakage-statement-page-size-label"
              />
            </Space>
          </Space>
        </Row>
      </Content>

      <BreakageLotDetailModal
        open={Boolean(detailLotId)}
        balanceLotId={detailLotId}
        onClose={() => setDetailLotId(null)}
      />
    </Layout>
  );
};

export default BreakageStatementScreen;
